import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import {
  getFlowSettings,
  setFlowSettings,
  getMessageRules,
  createMessageRule,
  updateMessageRule,
  deleteMessageRule,
  getTaskNomenclatures,
} from 'services/adminNegotiationService';
import { adminNegotiationI18n } from './i18n';
import TextArea from '../../components/TextArea/TextArea';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import AsyncStateView from '../../components/AsyncStateView/AsyncStateView';
import '../../styles/naEeppTableGrid.scss';

/** Lee el valor de un onChange que puede venir como evento (target.value) o valor crudo. */
const readVal = (e) =>
  e && typeof e === 'object' && 'target' in e ? e.target.value : e;

const onlyDigits = (v) => String(v ?? '').replace(/[^0-9]/g, '');

const RULE_TYPES = [
  'waiting',
  'approaching',
  'request',
  'reminder',
  'expired_rerequest',
  'user_rerequest',
  // Cierres terminales (G10): mensaje de término del proceso + mensaje feliz al aprobar la
  // documentación. Sin cadencia → fuera de RULE_NEEDS_DAYS; el sweep no los procesa.
  'cancellation',
  'documents_approved',
];
// Tipos que requieren daysValue (días antes del umbral / cadencia).
const RULE_NEEDS_DAYS = ['approaching', 'reminder'];
// Motivos de término (codes C2, labels vía i18n term_reason_*). Solo aplican a la regla
// `cancellation`: una regla por motivo (mensaje de cierre distinto por razón).
const CANCELLATION_REASONS = [
  'client_abandoned',
  'no_response',
  'service_change',
  'non_viable',
  'other',
];
const MESSAGE_MAX = 4000;

const blankSettings = {
  debtDaysNeeded: '',
  minCreditorsRequired: '',
  documentExpiryDays: '',
  triggerNomenclature: '',
  documentsTaskNomenclature: '',
  // Vencimiento de las tareas creadas por el flujo, en días desde hoy (vacío = null → la
  // tarea vence hoy, comportamiento actual). El nombre de la tarea sale de su nomenclatura.
  documentsTaskDueDays: '',
  finalTaskNomenclature: '',
  finalTaskDueDays: '',
};

const blankRule = {
  id: null,
  ruleType: 'waiting',
  daysValue: '',
  messageBody: '',
  active: true,
  cancellationReason: '',
};

/**
 * Pantalla de configuración del flujo de Negociación Administrativa (scaffold de
 * EscriturasFlags): sección de parámetros del flujo (condición para pedir documentos,
 * vigencia, tarea final) + sección de reglas de mensajes (CRUD sobre DS Table + Modal).
 *
 * Solo design system vía prop DesignSystemComponents + TextArea/ConfirmModal locales.
 */
export default function AdminNegotiationConfig({ DesignSystemComponents }) {
  const { t } = useScopedTranslation(adminNegotiationI18n);
  const {
    Button,
    Table,
    Badge,
    IconButton,
    Dropdown,
    Modal,
    Input,
    ComboBox,
    Card,
    Skeleton,
  } = DesignSystemComponents ?? {};

  const [settings, setSettings] = useState(blankSettings);
  const [rules, setRules] = useState([]);
  const [nomenclatures, setNomenclatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [editing, setEditing] = useState(null); // regla en edición (crear/editar)
  const [savingRule, setSavingRule] = useState(false); // usado por eliminar regla
  // Estado async del guardado de la regla (idle/loading/success/error) → body-swap (AsyncStateView).
  const [ruleStatus, setRuleStatus] = useState('idle');
  const [ruleError, setRuleError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchAll = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const [s, r] = await Promise.all([
        getFlowSettings(controller.signal),
        getMessageRules(controller.signal),
      ]);
      setSettings({
        debtDaysNeeded: s?.debtDaysNeeded ?? '',
        minCreditorsRequired: s?.minCreditorsRequired ?? '',
        documentExpiryDays: s?.documentExpiryDays ?? '',
        triggerNomenclature: s?.triggerNomenclature ?? '',
        documentsTaskNomenclature: s?.documentsTaskNomenclature ?? '',
        documentsTaskDueDays: s?.documentsTaskDueDays ?? '',
        finalTaskNomenclature: s?.finalTaskNomenclature ?? '',
        finalTaskDueDays: s?.finalTaskDueDays ?? '',
      });
      setRules(Array.isArray(r) ? r : []);
      // Las nomenclaturas alimentan el ComboBox de tarea final; su fallo no debe
      // vaciar la pantalla de configuración (el campo queda como texto editable).
      try {
        const n = await getTaskNomenclatures(controller.signal);
        setNomenclatures(Array.isArray(n) ? n : []);
      } catch (nomErr) {
        if (nomErr.name !== 'AbortError' && nomErr.name !== 'CanceledError')
          setNomenclatures([]);
      }
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(
        err.response?.data?.message || err.message || t('cfg_error_generic')
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
    return () => abortRef.current?.abort();
  }, [fetchAll]);

  useEffect(() => {
    if (!notice) return undefined;
    const id = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(id);
  }, [notice]);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    setError(null);
    try {
      const payload = {
        debtDaysNeeded:
          settings.debtDaysNeeded === ''
            ? null
            : Number(settings.debtDaysNeeded),
        minCreditorsRequired:
          settings.minCreditorsRequired === ''
            ? null
            : Number(settings.minCreditorsRequired),
        documentExpiryDays:
          settings.documentExpiryDays === ''
            ? null
            : Number(settings.documentExpiryDays),
        triggerNomenclature: settings.triggerNomenclature.trim() || null,
        documentsTaskNomenclature:
          settings.documentsTaskNomenclature.trim() || null,
        documentsTaskDueDays:
          settings.documentsTaskDueDays === ''
            ? null
            : Number(settings.documentsTaskDueDays),
        finalTaskNomenclature: settings.finalTaskNomenclature.trim() || null,
        finalTaskDueDays:
          settings.finalTaskDueDays === ''
            ? null
            : Number(settings.finalTaskDueDays),
      };
      const saved = await setFlowSettings(payload);
      if (!mountedRef.current) return;
      if (saved) {
        setSettings({
          debtDaysNeeded: saved.debtDaysNeeded ?? '',
          minCreditorsRequired: saved.minCreditorsRequired ?? '',
          documentExpiryDays: saved.documentExpiryDays ?? '',
          triggerNomenclature: saved.triggerNomenclature ?? '',
          documentsTaskNomenclature: saved.documentsTaskNomenclature ?? '',
          documentsTaskDueDays: saved.documentsTaskDueDays ?? '',
          finalTaskNomenclature: saved.finalTaskNomenclature ?? '',
          finalTaskDueDays: saved.finalTaskDueDays ?? '',
        });
      }
      setNotice(t('cfg_saved'));
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(
        err.response?.data?.message || err.message || t('cfg_error_generic')
      );
    } finally {
      if (mountedRef.current) setSavingSettings(false);
    }
  };

  const ruleTypeLabel = useCallback((type) => t(`cfg_rule_type_${type}`), [t]);

  const ruleNeedsDays = RULE_NEEDS_DAYS.includes(editing?.ruleType);

  const canSaveRule =
    !!editing &&
    (editing.messageBody || '').trim().length > 0 &&
    (!RULE_NEEDS_DAYS.includes(editing.ruleType) ||
      (editing.daysValue !== '' && Number(editing.daysValue) > 0)) &&
    (editing.ruleType !== 'cancellation' ||
      (editing.cancellationReason || '').trim().length > 0);

  const handleSaveRule = async () => {
    if (!canSaveRule || ruleStatus === 'loading') {
      // Validación pre-submit: se queda inline en el idle (no salta al estado error).
      if (!canSaveRule) setError(t('cfg_required_error'));
      return;
    }
    setRuleStatus('loading');
    setRuleError(null);
    setError(null);
    try {
      const payload = {
        ruleType: editing.ruleType,
        daysValue: RULE_NEEDS_DAYS.includes(editing.ruleType)
          ? Number(editing.daysValue)
          : null,
        messageBody: editing.messageBody.trim(),
        active: editing.active,
        cancellationReason:
          editing.ruleType === 'cancellation'
            ? editing.cancellationReason
            : null,
      };
      if (editing.id) {
        await updateMessageRule(editing.id, payload);
      } else {
        await createMessageRule(payload);
      }
      if (mountedRef.current) setRuleStatus('success');
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      if (!mountedRef.current) return;
      setRuleError(
        err.response?.data?.message || err.message || t('cfg_error_generic')
      );
      setRuleStatus('error');
    }
  };

  const handleDeleteRule = async () => {
    if (!confirmDelete) return;
    setSavingRule(true);
    try {
      await deleteMessageRule(confirmDelete.id);
      if (!mountedRef.current) return;
      setConfirmDelete(null);
      await fetchAll();
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      if (!mountedRef.current) return;
      setError(
        err.response?.data?.message || err.message || t('cfg_error_generic')
      );
    } finally {
      if (mountedRef.current) setSavingRule(false);
    }
  };

  const ruleTypeOptions = useMemo(
    () => RULE_TYPES.map((rt) => ({ label: ruleTypeLabel(rt), value: rt })),
    [ruleTypeLabel]
  );

  // Opciones del Dropdown de Estado (patrón CreateEditCourtModal): booleano directo
  // mapeado al flag `active` del payload. Reusa las keys de badge ya existentes.
  const statusOptions = useMemo(
    () => [
      { label: t('cfg_rule_active_yes'), value: true },
      { label: t('cfg_rule_active_no'), value: false },
    ],
    [t]
  );

  // Motivos del Dropdown de término (solo regla cancellation); labels reusan term_reason_*.
  const reasonOptions = useMemo(
    () =>
      CANCELLATION_REASONS.map((code) => ({
        label: t(`term_reason_${code}`),
        value: code,
      })),
    [t]
  );

  // Opciones del ComboBox de nomenclatura: value = code. El nombre de la tarea creada sale
  // de la nomenclatura en el back, así que acá el name solo compone el label.
  const nomenclatureOptions = useMemo(
    () =>
      nomenclatures
        .filter((n) => n?.code)
        .map((n) => ({
          label: n.name ? `${n.code} — ${n.name}` : n.code,
          value: n.code,
        })),
    [nomenclatures]
  );

  // Opción seleccionada a partir del code guardado; si no está en la lista (código
  // legado) se muestra el code crudo para no perderlo.
  const selectedNomenclature = useMemo(() => {
    const code = settings.finalTaskNomenclature;
    if (!code) return null;
    return (
      nomenclatureOptions.find((o) => o.value === code) || {
        label: code,
        value: code,
      }
    );
  }, [settings.finalTaskNomenclature, nomenclatureOptions]);

  // Trigger (tarea 1): single-select sobre el mismo catálogo de nomenclaturas (la tarea
  // trigger la crea GC o el alta manual).
  const selectedTriggerNomenclature = useMemo(() => {
    const code = settings.triggerNomenclature;
    if (!code) return null;
    return (
      nomenclatureOptions.find((o) => o.value === code) || {
        label: code,
        value: code,
      }
    );
  }, [settings.triggerNomenclature, nomenclatureOptions]);

  // Tarea de documentos (tarea 2): espejo del par final (code + vencimiento en días).
  const selectedDocumentsNomenclature = useMemo(() => {
    const code = settings.documentsTaskNomenclature;
    if (!code) return null;
    return (
      nomenclatureOptions.find((o) => o.value === code) || {
        label: code,
        value: code,
      }
    );
  }, [settings.documentsTaskNomenclature, nomenclatureOptions]);

  const activeBadge = (r) => {
    const text = r.active ? t('cfg_rule_active_yes') : t('cfg_rule_active_no');
    const color = r.active ? 'success' : 'light';
    return Badge ? (
      <Badge text={text} color={color} size='sm' />
    ) : (
      <span>{text}</span>
    );
  };

  const bodyExcerpt = (value) => {
    if (!value) return '—';
    const clean = value.trim();
    return clean.length > 90 ? `${clean.slice(0, 90)}…` : clean;
  };

  const columns = [
    {
      accessor: 'ruleType',
      label: t('cfg_rule_type'),
      // En reglas de término se muestra el motivo junto al tipo (una regla por motivo).
      render: (r) =>
        r.ruleType === 'cancellation' && r.cancellationReason ? (
          <span>
            {ruleTypeLabel(r.ruleType)}{' '}
            <span className='font-light'>
              · {t(`term_reason_${r.cancellationReason}`)}
            </span>
          </span>
        ) : (
          ruleTypeLabel(r.ruleType)
        ),
    },
    {
      accessor: 'daysValue',
      label: t('cfg_rule_days'),
      render: (r) => (r.daysValue != null ? r.daysValue : '—'),
    },
    {
      accessor: 'messageBody',
      label: t('cfg_rule_body'),
      render: (r) => (
        <span title={r.messageBody || ''}>{bodyExcerpt(r.messageBody)}</span>
      ),
    },
    { accessor: 'active', label: t('cfg_rule_active'), render: activeBadge },
    {
      label: t('cfg_rule_col_actions'),
      render: (r) => (
        <div className='d-flex gap-2 align-items-center'>
          {IconButton ? (
            <IconButton
              ariaLabel={t('cfg_rule_edit')}
              icon='pencil'
              size='sm'
              stroke='var(--primary-500)'
              onClick={() => {
                setRuleStatus('idle');
                setRuleError(null);
                setEditing({
                  ...r,
                  daysValue: r.daysValue ?? '',
                  cancellationReason: r.cancellationReason ?? '',
                });
              }}
            />
          ) : (
            <button
              type='button'
              className='btn btn-link btn-sm'
              onClick={() => {
                setRuleStatus('idle');
                setRuleError(null);
                setEditing({
                  ...r,
                  daysValue: r.daysValue ?? '',
                  cancellationReason: r.cancellationReason ?? '',
                });
              }}
            >
              {t('cfg_rule_edit')}
            </button>
          )}
          {IconButton && (
            <IconButton
              ariaLabel={t('cfg_rule_delete')}
              icon='trash'
              size='sm'
              stroke='var(--error-500)'
              onClick={() => setConfirmDelete(r)}
            />
          )}
        </div>
      ),
    },
  ];

  const ContentWrapper = Card || 'div';
  const contentWrapperProps = Card
    ? { specialPadding: 'p-4' }
    : { className: 'container-fluid p-4' };

  const ruleFormBody = editing && (
    <AsyncStateView
      status={ruleStatus}
      loadingTitle={t('cfg_state_loading')}
      successTitle={t('cfg_state_success')}
      errorMessage={ruleError}
      onBack={() => setRuleStatus('idle')}
      onSuccessAutoClose={() => {
        setEditing(null);
        fetchAll();
      }}
    >
      <div>
        {error && (
          <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
            {error}
          </div>
        )}
        <div className='d-flex flex-column gap-3'>
          {Dropdown && (
            <div>
              <label className='caption font-light d-block mb-1'>
                {t('cfg_rule_type')}
              </label>
              <Dropdown
                options={ruleTypeOptions}
                value={
                  ruleTypeOptions.find((o) => o.value === editing.ruleType) ||
                  ruleTypeOptions[0]
                }
                onChange={(o) =>
                  setEditing((s) => ({ ...s, ruleType: o?.value ?? 'waiting' }))
                }
                width='100%'
              />
            </div>
          )}
          {editing.ruleType === 'cancellation' && Dropdown && (
            <div>
              <label className='caption font-light d-block mb-1'>
                {t('term_reason_label')}
              </label>
              <Dropdown
                options={reasonOptions}
                value={
                  reasonOptions.find(
                    (o) => o.value === editing.cancellationReason
                  ) ?? null
                }
                onChange={(o) =>
                  setEditing((s) => ({
                    ...s,
                    cancellationReason: o?.value ?? '',
                  }))
                }
                placeholder={t('term_reason_label')}
                width='100%'
              />
            </div>
          )}
          {ruleNeedsDays && Input && (
            <Input
              label={t('cfg_rule_days')}
              type='number'
              value={editing.daysValue}
              onChange={(e) =>
                setEditing((s) => ({ ...s, daysValue: onlyDigits(readVal(e)) }))
              }
              disabled={savingRule}
              helperText={t('cfg_rule_days_help')}
              width='100%'
            />
          )}
          <TextArea
            label={t('cfg_rule_body')}
            value={editing.messageBody}
            onChange={(e) =>
              setEditing((s) => ({ ...s, messageBody: readVal(e) }))
            }
            disabled={savingRule}
            rows={4}
            maxLength={MESSAGE_MAX}
            showCount
          />
          {Dropdown && (
            <div>
              <label className='form-label'>{t('cfg_estado_label')}</label>
              <Dropdown
                options={statusOptions}
                value={
                  statusOptions.find((o) => o.value === !!editing.active) ??
                  statusOptions[0]
                }
                onChange={(o) =>
                  setEditing((s) => ({ ...s, active: o?.value ?? true }))
                }
                width='100%'
              />
            </div>
          )}
        </div>
        <div className='d-flex justify-content-end gap-2 mt-3'>
          {Button && (
            <Button
              label={t('cfg_cancel')}
              size='sm'
              variant='secondary'
              onClick={() => {
                setEditing(null);
                setError(null);
              }}
              disabled={savingRule}
            />
          )}
          {Button && (
            <Button
              label={savingRule ? t('cfg_loading') : t('cfg_save')}
              size='sm'
              variant='primary'
              onClick={handleSaveRule}
              disabled={!canSaveRule || savingRule}
            />
          )}
        </div>
      </div>
    </AsyncStateView>
  );

  return (
    <div>
      <div className='mb-4 d-flex justify-content-between align-items-start'>
        <div>
          <h2 className='fw-semibold'>{t('cfg_title')}</h2>
          <h4 className='fw-normal'>{t('cfg_subtitle')}</h4>
        </div>
      </div>

      <ContentWrapper {...contentWrapperProps}>
        {notice && (
          <div className='alert alert-success py-2' style={{ fontSize: 13 }}>
            {notice}
          </div>
        )}
        {error && !editing && (
          <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div className='d-flex flex-column gap-2'>
            {Skeleton ? (
              [0, 1, 2].map((i) => (
                <Skeleton key={i} height='2.5rem' width='100%' />
              ))
            ) : (
              <p className='font-light'>{t('cfg_loading')}</p>
            )}
          </div>
        ) : (
          <>
            {/* Parámetros del flujo */}
            <h5 className='fw-semibold mb-2' style={{ fontSize: 14 }}>
              {t('cfg_settings_title')}
            </h5>
            <div
              className='d-flex flex-wrap gap-3 mb-2'
              style={{ maxWidth: 720 }}
            >
              {Input && (
                <div style={{ flex: '1 1 200px' }}>
                  <Input
                    label={t('cfg_days_needed')}
                    type='number'
                    value={settings.debtDaysNeeded}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        debtDaysNeeded: onlyDigits(readVal(e)),
                      }))
                    }
                    disabled={savingSettings}
                    width='100%'
                  />
                </div>
              )}
              {Input && (
                <div style={{ flex: '1 1 200px' }}>
                  <Input
                    label={t('cfg_min_creditors')}
                    type='number'
                    value={settings.minCreditorsRequired}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        minCreditorsRequired: onlyDigits(readVal(e)),
                      }))
                    }
                    disabled={savingSettings}
                    width='100%'
                  />
                </div>
              )}
              {Input && (
                <div style={{ flex: '1 1 200px' }}>
                  <Input
                    label={t('cfg_expiry_days')}
                    type='number'
                    value={settings.documentExpiryDays}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        documentExpiryDays: onlyDigits(readVal(e)),
                      }))
                    }
                    disabled={savingSettings}
                    width='100%'
                  />
                </div>
              )}
            </div>
            {/* Trigger (tarea 1): nomenclatura que inicia el proceso NA. Single-select
              sobre el mismo catálogo que la tarea final; el PUT single sobrescribe el CSV. */}
            <div
              className='d-flex flex-wrap gap-3 mb-3'
              style={{ maxWidth: 720 }}
            >
              {ComboBox ? (
                <div style={{ flex: '1 1 300px' }}>
                  <label className='caption font-light d-block mb-1'>
                    {t('cfg_trigger_nomenclature_label')}
                  </label>
                  <ComboBox
                    placeholder={t('cfg_final_task_nomenclature_placeholder')}
                    noResultText={t('cfg_final_task_nomenclature_empty')}
                    options={nomenclatureOptions}
                    value={selectedTriggerNomenclature}
                    onChange={(option) =>
                      setSettings((s) => ({
                        ...s,
                        triggerNomenclature: option?.value ?? '',
                      }))
                    }
                    isClearable
                    width='100%'
                  />
                </div>
              ) : (
                Input && (
                  <div style={{ flex: '1 1 300px' }}>
                    <Input
                      label={t('cfg_trigger_nomenclature_label')}
                      value={settings.triggerNomenclature}
                      onChange={(e) =>
                        setSettings((s) => ({
                          ...s,
                          triggerNomenclature: readVal(e),
                        }))
                      }
                      disabled={savingSettings}
                      maxLength={100}
                      width='100%'
                    />
                  </div>
                )
              )}
            </div>

            {/* Tarea de documentos (tarea 2): espejo del par final (nomenclatura +
              vencimiento en días). Si la nomenclatura queda vacía, el pipeline no crea la
              tarea; el nombre de la tarea creada = el nombre de la nomenclatura. */}
            <div
              className='d-flex flex-wrap gap-3 mb-3'
              style={{ maxWidth: 720 }}
            >
              {ComboBox ? (
                <div style={{ flex: '1 1 300px' }}>
                  <label className='caption font-light d-block mb-1'>
                    {t('cfg_docs_task_nomenclature_label')}
                  </label>
                  <ComboBox
                    placeholder={t('cfg_final_task_nomenclature_placeholder')}
                    noResultText={t('cfg_final_task_nomenclature_empty')}
                    options={nomenclatureOptions}
                    value={selectedDocumentsNomenclature}
                    onChange={(option) =>
                      setSettings((s) => ({
                        ...s,
                        documentsTaskNomenclature: option?.value ?? '',
                      }))
                    }
                    isClearable
                    width='100%'
                  />
                </div>
              ) : (
                Input && (
                  <div style={{ flex: '1 1 300px' }}>
                    <Input
                      label={t('cfg_docs_task_nomenclature_label')}
                      value={settings.documentsTaskNomenclature}
                      onChange={(e) =>
                        setSettings((s) => ({
                          ...s,
                          documentsTaskNomenclature: readVal(e),
                        }))
                      }
                      disabled={savingSettings}
                      maxLength={100}
                      width='100%'
                    />
                  </div>
                )
              )}
              {Input && (
                <div style={{ flex: '1 1 300px' }}>
                  <Input
                    label={t('cfg_docs_task_due_days_label')}
                    type='number'
                    value={settings.documentsTaskDueDays}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        documentsTaskDueDays: onlyDigits(readVal(e)),
                      }))
                    }
                    disabled={savingSettings}
                    width='100%'
                  />
                </div>
              )}
            </div>

            <div
              className='d-flex flex-wrap gap-3 mb-3'
              style={{ maxWidth: 720 }}
            >
              {ComboBox ? (
                <div style={{ flex: '1 1 300px' }}>
                  <label className='caption font-light d-block mb-1'>
                    {t('cfg_final_task_nomenclature')}
                  </label>
                  <ComboBox
                    placeholder={t('cfg_final_task_nomenclature_placeholder')}
                    noResultText={t('cfg_final_task_nomenclature_empty')}
                    options={nomenclatureOptions}
                    value={selectedNomenclature}
                    onChange={(option) =>
                      setSettings((s) => ({
                        ...s,
                        finalTaskNomenclature: option?.value ?? '',
                      }))
                    }
                    isClearable
                    width='100%'
                  />
                </div>
              ) : (
                Input && (
                  <div style={{ flex: '1 1 300px' }}>
                    <Input
                      label={t('cfg_final_task_nomenclature')}
                      value={settings.finalTaskNomenclature}
                      onChange={(e) =>
                        setSettings((s) => ({
                          ...s,
                          finalTaskNomenclature: readVal(e),
                        }))
                      }
                      disabled={savingSettings}
                      maxLength={100}
                      width='100%'
                    />
                  </div>
                )
              )}
              {Input && (
                <div style={{ flex: '1 1 300px' }}>
                  <Input
                    label={t('cfg_final_task_due_days_label')}
                    type='number'
                    value={settings.finalTaskDueDays}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        finalTaskDueDays: onlyDigits(readVal(e)),
                      }))
                    }
                    disabled={savingSettings}
                    width='100%'
                  />
                </div>
              )}
            </div>
            <div className='mb-4'>
              {Button && (
                <Button
                  label={savingSettings ? t('cfg_loading') : t('cfg_save')}
                  size='sm'
                  variant='primary'
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                />
              )}
            </div>

            {/* Reglas de mensajes */}
            <div className='d-flex justify-content-between align-items-start mb-2'>
              <div>
                <h5 className='fw-semibold mb-1' style={{ fontSize: 14 }}>
                  {t('cfg_rules_title')}
                </h5>
                <p
                  className='caption font-light mb-0'
                  style={{ maxWidth: 720 }}
                >
                  {t('cfg_rules_subtitle')}
                </p>
              </div>
              {Button && (
                <Button
                  label={t('cfg_rule_add')}
                  size='sm'
                  variant='primary'
                  onClick={() => {
                    setError(null);
                    setRuleStatus('idle');
                    setRuleError(null);
                    setEditing({ ...blankRule });
                  }}
                />
              )}
            </div>

            <div className='table-container na-eepp-grid-5'>
              {Table ? (
                <Table columns={columns} data={rules} loading={loading} />
              ) : null}
              {rules.length === 0 && (
                <div className='text-center p-4'>
                  <p className='caption font-light'>{t('cfg_rule_empty')}</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Modal crear/editar regla */}
        {editing && Modal && (
          <Modal
            title={
              editing.id ? t('cfg_rule_edit_title') : t('cfg_rule_create_title')
            }
            show
            onClose={() => {
              if (ruleStatus === 'loading' || ruleStatus === 'success') return;
              setEditing(null);
              setError(null);
            }}
            primaryBtnTxt={null}
            secondaryBtnTxt={null}
            size='md'
          >
            {ruleFormBody}
          </Modal>
        )}

        <ConfirmModal
          Modal={Modal}
          show={!!confirmDelete}
          title={t('cfg_rule_delete_title')}
          message={t('cfg_rule_delete_message')}
          confirmLabel={savingRule ? t('cfg_loading') : t('cfg_rule_delete')}
          cancelLabel={t('cfg_cancel')}
          confirmVariant='danger'
          onConfirm={handleDeleteRule}
          onClose={() => setConfirmDelete(null)}
        />
      </ContentWrapper>
    </div>
  );
}
